import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { userService } from '../../services/user.service';
import api from '../../services/api';
import { FaDownload, FaChartLine, FaSpinner, FaCalendar, FaUser, FaEnvelope } from 'react-icons/fa';

const EmployeePerformance = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);

    useEffect(() => {
        fetchUsers();
        setDefaultDates();
    }, []);

    const setDefaultDates = () => {
        const today = new Date();
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

        setEndDate(today.toISOString().split('T')[0]);
        setStartDate(lastMonth.toISOString().split('T')[0]);
    };

    const fetchUsers = async () => {
        try {
            const data = await userService.getAllUsers({ status: 'active' });
            setUsers(data.users || []);
        } catch (error) {
            toast.error('Failed to fetch users');
        }
    };

    const fetchAnalytics = async () => {
        if (!selectedUser) {
            toast.error('Please select an employee');
            return;
        }

        if (!startDate || !endDate) {
            toast.error('Please select date range');
            return;
        }

        try {
            setLoading(true);
            const response = await api.get(`/analytics/employee/${selectedUser}`, {
                params: { startDate, endDate }
            });

            if (response.data.success) {
                setAnalytics(response.data.data);
            }
        } catch (error) {
            toast.error('Failed to fetch analytics');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const generatePDFReport = async () => {
        if (!selectedUser) {
            toast.error('Please select an employee');
            return;
        }

        if (!startDate || !endDate) {
            toast.error('Please select date range');
            return;
        }

        try {
            setGenerating(true);
            const response = await api.post('/analytics/report/generate', {
                userId: selectedUser,
                startDate,
                endDate,
                sendEmail: sendEmail
            }, {
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `performance-report-${Date.now()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            if (sendEmail) {
                toast.success('📊 PDF generated and emailed to employee successfully!', { autoClose: 5000 });
            } else {
                toast.success('PDF report generated successfully!');
            }
        } catch (error) {
            toast.error('Failed to generate PDF report');
            console.error(error);
        } finally {
            setGenerating(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 60) return 'text-blue-600';
        if (score >= 40) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getScoreBgColor = (score) => {
        if (score >= 80) return 'bg-green-100';
        if (score >= 60) return 'bg-blue-100';
        if (score >= 40) return 'bg-yellow-100';
        return 'bg-red-100';
    };

    const getScoreLabel = (score) => {
        if (score >= 80) return 'EXCELLENT';
        if (score >= 60) return 'GOOD';
        if (score >= 40) return 'SATISFACTORY';
        return 'NEEDS IMPROVEMENT';
    };

    const getSelectedUserEmail = () => {
        const user = users.find(u => u._id === selectedUser);
        return user?.email || 'employee email';
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold">Employee Performance Analytics</h1>
                <p className="text-gray-600 mt-2">Analyze employee performance with AI-powered insights and beautiful reports</p>
            </div>

            {/* Selection Panel */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            <FaUser className="inline mr-2" />
                            Select Employee *
                        </label>
                        <select
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Choose Employee</option>
                            {users.map(user => (
                                <option key={user._id} value={user._id}>
                                    {user.name} - {user.role?.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            <FaCalendar className="inline mr-2" />
                            Start Date *
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            <FaCalendar className="inline mr-2" />
                            End Date *
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={fetchAnalytics}
                            disabled={loading}
                            className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="animate-spin" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <FaChartLine />
                                    Analyze
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Analytics Display */}
            {analytics && (
                <>
                    {/* Employee Info Card */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 mb-6 text-white">
                        <div className="flex justify-between items-start">
                            <div>
                                <h2 className="text-2xl font-bold">{analytics.user.name}</h2>
                                <p className="text-blue-100 mt-1">{analytics.user.email}</p>
                                <div className="flex gap-4 mt-3">
                                    <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                                        {analytics.user.role}
                                    </span>
                                    <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                                        {analytics.user.department}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-blue-100 text-sm">Review Period</p>
                                <p className="font-medium">{new Date(analytics.dateRange.start).toLocaleDateString()}</p>
                                <p className="font-medium">{new Date(analytics.dateRange.end).toLocaleDateString()}</p>
                                <p className="text-blue-100 text-xs mt-1">{analytics.dateRange.days} days</p>
                            </div>
                        </div>
                    </div>

                    {/* Performance Score */}
                    <div className="bg-white rounded-lg shadow p-8 mb-6 text-center">
                        <h3 className="text-xl font-semibold mb-4">Overall Performance Score</h3>
                        <div className={`inline-flex items-center justify-center w-40 h-40 rounded-full ${getScoreBgColor(analytics.performanceScore)}`}>
                            <span className={`text-6xl font-bold ${getScoreColor(analytics.performanceScore)}`}>
                                {analytics.performanceScore}
                            </span>
                        </div>
                        <p className={`text-xl font-bold mt-4 ${getScoreColor(analytics.performanceScore)}`}>
                            {getScoreLabel(analytics.performanceScore)}
                        </p>
                        <p className="text-gray-600 mt-2">
                            Period: {new Date(analytics.dateRange.start).toLocaleDateString()} - {new Date(analytics.dateRange.end).toLocaleDateString()}
                        </p>
                        <p className="text-gray-500">
                            {analytics.dateRange.days} days analyzed
                        </p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        {/* Task Stats */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h4 className="text-sm font-medium text-gray-500 mb-4">TASK PERFORMANCE</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Tasks</span>
                                    <span className="font-bold">{analytics.tasks.total}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Completed</span>
                                    <span className="font-bold text-green-600">{analytics.tasks.completed}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">In Progress</span>
                                    <span className="font-bold text-blue-600">{analytics.tasks.inProgress}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Pending</span>
                                    <span className="font-bold text-yellow-600">{analytics.tasks.pending}</span>
                                </div>
                                <div className="pt-3 border-t">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Completion Rate</span>
                                        <span className="font-bold text-blue-600">{analytics.tasks.completionRate}%</span>
                                    </div>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">On-Time Rate</span>
                                    <span className="font-bold text-green-600">{analytics.tasks.onTimeRate}%</span>
                                </div>
                            </div>
                        </div>

                        {/* Attendance Stats */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h4 className="text-sm font-medium text-gray-500 mb-4">ATTENDANCE</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Days</span>
                                    <span className="font-bold">{analytics.attendance.totalDays}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Present</span>
                                    <span className="font-bold text-green-600">{analytics.attendance.present}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Absent</span>
                                    <span className="font-bold text-red-600">{analytics.attendance.absent}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Late</span>
                                    <span className="font-bold text-yellow-600">{analytics.attendance.late}</span>
                                </div>
                                <div className="pt-3 border-t">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Attendance Rate</span>
                                        <span className="font-bold text-blue-600">{analytics.attendance.attendanceRate}%</span>
                                    </div>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Avg Work Hours</span>
                                    <span className="font-bold text-blue-600">{analytics.attendance.avgWorkHours}h</span>
                                </div>
                            </div>
                        </div>

                        {/* Time Stats */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h4 className="text-sm font-medium text-gray-500 mb-4">TIME METRICS</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">On Time</span>
                                    <span className="font-bold text-green-600">{analytics.tasks.onTime}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Overdue</span>
                                    <span className="font-bold text-red-600">{analytics.tasks.overdue}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Avg Work Hours</span>
                                    <span className="font-bold">{analytics.attendance.avgWorkHours}h</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Avg Completion</span>
                                    <span className="font-bold">{analytics.tasks.avgCompletionTime}d</span>
                                </div>
                                <div className="pt-3 border-t">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">On-Time Rate</span>
                                        <span className="font-bold text-blue-600">{analytics.tasks.onTimeRate}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Leave Stats */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h4 className="text-sm font-medium text-gray-500 mb-4">LEAVES</h4>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Requests</span>
                                    <span className="font-bold">{analytics.leaves.total}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Approved</span>
                                    <span className="font-bold text-green-600">{analytics.leaves.approved}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Pending</span>
                                    <span className="font-bold text-yellow-600">{analytics.leaves.pending}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Rejected</span>
                                    <span className="font-bold text-red-600">{analytics.leaves.rejected}</span>
                                </div>
                                <div className="pt-3 border-t">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Total Days</span>
                                        <span className="font-bold text-blue-600">{analytics.leaves.totalDays}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Client Performance (if available) */}
                    {analytics.clients && (
                        <div className="bg-white rounded-lg shadow p-6 mb-6">
                            <h3 className="text-xl font-semibold mb-4">Client Performance</h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                                <div className="text-center">
                                    <p className="text-3xl font-bold text-blue-600">{analytics.clients.total}</p>
                                    <p className="text-gray-600 mt-1">Total Clients</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl font-bold text-green-600">{analytics.clients.converted}</p>
                                    <p className="text-gray-600 mt-1">Converted</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl font-bold text-yellow-600">{analytics.clients.interested}</p>
                                    <p className="text-gray-600 mt-1">Interested</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl font-bold text-gray-600">{analytics.clients.pending}</p>
                                    <p className="text-gray-600 mt-1">Pending</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl font-bold text-purple-600">{analytics.clients.conversionRate}%</p>
                                    <p className="text-gray-600 mt-1">Conversion Rate</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Email Option Checkbox */}
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6 mb-4">
                        <label className="flex items-start cursor-pointer">
                            <input
                                type="checkbox"
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                                className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="ml-4">
                                <div className="flex items-center gap-2">
                                    <FaEnvelope className="text-blue-600" />
                                    <span className="text-lg font-semibold text-gray-900">
                                        📧 Email Report to Employee
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    {sendEmail
                                        ? `✓ Beautiful PDF report will be sent to: ${getSelectedUserEmail()}`
                                        : 'Check this box to automatically email the performance report to the employee'
                                    }
                                </p>
                                {sendEmail && (
                                    <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                                        <p className="text-xs text-blue-800">
                                            <strong>What the employee will receive:</strong>
                                            <br />
                                            • Beautiful HTML email with performance summary
                                            <br />
                                            • 5-page colorful PDF report attached
                                            <br />
                                            • AI-powered insights and recommendations
                                        </p>
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-white border-2 border-blue-200 rounded-lg p-6 text-center shadow-lg">
                        <p className="text-lg text-gray-800 mb-4 font-medium">
                            {sendEmail
                                ? '📊 Generate beautiful PDF report and email to employee'
                                : '📊 Want detailed AI-powered insights and recommendations?'
                            }
                        </p>
                        <button
                            onClick={generatePDFReport}
                            disabled={generating}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-lg hover:from-blue-700 hover:to-purple-700 font-semibold text-lg inline-flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transform transition hover:scale-105"
                        >
                            {generating ? (
                                <>
                                    <FaSpinner className="animate-spin text-xl" />
                                    {sendEmail ? 'Generating & Sending Email...' : 'Generating Report...'}
                                </>
                            ) : (
                                <>
                                    <FaDownload className="text-xl" />
                                    {sendEmail ? 'Generate & Email Report' : 'Download Beautiful PDF Report'}
                                </>
                            )}
                        </button>
                        {sendEmail && (
                            <p className="text-sm text-gray-600 mt-4">
                                💌 Report will be emailed to <strong>{getSelectedUserEmail()}</strong> and also downloaded to your device
                            </p>
                        )}
                        {!sendEmail && (
                            <p className="text-xs text-gray-500 mt-4">
                                The PDF will include 5 beautifully designed pages with AI-powered analysis
                            </p>
                        )}
                    </div>
                </>
            )}

            {/* Empty State */}
            {!analytics && !loading && (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <FaChartLine className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        No Analytics Data
                    </h3>
                    <p className="text-gray-500">
                        Select an employee and date range to view performance analytics
                    </p>
                </div>
            )}
        </div>
    );
};

export default EmployeePerformance;